import React, { useCallback, useEffect, useState } from 'react';
import { Select, Divider, Input, Button, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../variables/api/axiosInstance';

/**
 * Liste déroulante alimentée par une liste paramétrable, et qui sait s'allonger.
 *
 * POURQUOI UN AJOUT SUR PLACE. Ces listes - objets de courrier, natures - sont
 * ouvertes par nature : un bureau en découvre une nouvelle en pleine saisie.
 * L'envoyer dans Paramètres → Listes pour la créer lui fait perdre la saisie en
 * cours, et il finit par écrire au clavier une valeur qui ne rejoindra jamais la
 * liste. On ajoute donc là où le besoin apparaît.
 *
 * POURQUOI LE CODE EST DÉRIVÉ DU LIBELLÉ. Le code compose la clé unique du
 * paramètre et reste obligatoire en base ; le demander à la saisie ferait
 * répondre « OBJ1 », « OBJ2 » à un agent pressé. « Accusé de réception » devient
 * ACCUSE_DE_RECEPTION - lisible dans la table des paramètres, où quelqu'un
 * devra un jour retrouver l'entrée.
 *
 * LA VALEUR STOCKÉE EST LE LIBELLÉ, pas le code : le champ visé côté métier est
 * du texte libre (l'objet d'un courrier), et y mettre un code obligerait chaque
 * écran d'affichage à connaître la table pour le rendre lisible.
 */
export default function SelectListeParametrable({
  categorie,
  value,
  onChange,
  placeholder,
  style,
  disabled = false,
}) {
  const { t } = useTranslation('commun');
  const [options, setOptions] = useState([]);
  const [nouveau, setNouveau] = useState('');
  const [enregistrement, setEnregistrement] = useState(false);

  const charger = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/v1/parametres?type=LISTE', { skipErrorToast: true });
      const prefixe = `${categorie.toUpperCase()}.`;
      setOptions(
        (res.data?.data || [])
          .filter((p) => (p.cle || '').toUpperCase().startsWith(prefixe))
          .map((p) => ({ value: p.valeur, label: p.valeur })),
      );
    } catch {
      /* La liste reste vide, mais le champ demeure utilisable : le `Select` est
         en saisie libre, et un incident de chargement ne doit pas empêcher
         d'enregistrer un bordereau. */
      setOptions([]);
    }
  }, [categorie]);

  useEffect(() => { charger(); }, [charger]);

  /** « Accusé de réception » → ACCUSE_DE_RECEPTION. */
  const deriverCode = (libelle) => (libelle || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

  const ajouter = async () => {
    const libelle = nouveau.trim();
    if (!libelle) return;
    const code = deriverCode(libelle);
    if (!code) { toast.error(t('selectListeParametrable.libelleInvalide')); return; }

    setEnregistrement(true);
    try {
      await axiosInstance.post('/v1/parametres', {
        typeParametre: 'LISTE',
        cle: `${categorie.toUpperCase()}.${code}`,
        valeur: libelle,
        description: null,
      });
      await charger();
      // Sélectionné dans la foulée : on ne l'a pas créé pour aller le chercher.
      onChange?.(libelle);
      setNouveau('');
      toast.success(t('selectListeParametrable.ajoute'));
    } catch (e) {
      toast.error(e?.response?.data?.message || t('selectListeParametrable.ajoutImpossible'));
    } finally {
      setEnregistrement(false);
    }
  };

  return (
    <Select
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder === undefined ? t('selectListeParametrable.selectionner') : placeholder}
      style={style}
      disabled={disabled}
      allowClear
      showSearch
      optionFilterProp="label"
      options={options}
      /* La saisie libre reste possible : une valeur exceptionnelle n'a pas
         vocation à rejoindre la liste, et forcer son ajout la polluerait. */
      dropdownRender={(menu) => (
        <>
          {menu}
          <Divider style={{ margin: '6px 0' }} />
          <Space.Compact style={{ width: '100%', padding: '0 6px 6px' }}>
            <Input
              size="small"
              value={nouveau}
              placeholder={t('selectListeParametrable.ajouterALaListe')}
              onChange={(e) => setNouveau(e.target.value)}
              // Le clic dans le champ ne doit pas refermer la liste.
              onMouseDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
            />
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              loading={enregistrement}
              onMouseDown={(e) => e.preventDefault()}
              onClick={ajouter}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
            />
          </Space.Compact>
        </>
      )}
    />
  );
}
